import { Injectable, OnDestroy } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LocalStorageService } from './storage/local-storage.service';
import { SessionStorageService } from './storage/session-storage.service';
import { KEYS } from '../utils/constants/storage.constants';

/**
 * Interface for session state
 */
interface SessionState {
  accountId: string | undefined;
  email: string | undefined;
}

/**
 * Service responsible for managing user session state and session-related operations.
 * Handles session ID generation, storage, and retrieval.
 *
 * @class SessionService
 */
@Injectable({
  providedIn: 'root',
})
export class SessionService implements OnDestroy {
  /** Current account ID */
  public accountId: string | undefined;

  /** Current email */
  public email: string | undefined;

  /** BehaviorSubject for session state */
  private session$ = new BehaviorSubject<SessionState>({
    accountId: undefined,
    email: undefined,
  });

  /** Subject for cleanup */
  private destroy$ = new Subject<void>();

  /**
   * Initializes the service with required dependencies
   * @param localStorage - Service for managing local storage
   * @param sessionStorage - Service for managing session storage
   */
  constructor(
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService
  ) {
    this.initializeSessionSubscription();
  }

  /**
   * Initializes the session subscription
   * Updates accountId and email when session state changes
   */
  private initializeSessionSubscription(): void {
    this.session$
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.accountId = result.accountId;
        this.email = result.email;
      });
  }

  /**
   * Cleans up resources when the service is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Retrieves the current session ID
   * @returns string - The session ID with hyphens removed
   */
  getSessionID(): string {
    const sessionId = this.sessionStorage.getItem(KEYS.SESSION_ID);
    return sessionId ? sessionId.replace(/-/g, '') : '';
  }

  /**
   * Sets a new session ID based on the access token
   * @param newAccessToken - The new access token to generate session ID from
   */
  setSessionID(newAccessToken: string): void {
    if (!newAccessToken) {
      console.error('Access token is required to set session ID');
      return;
    }

    try {
      const hash = this.generateSessionHash(newAccessToken);
      this.sessionStorage.setItem(KEYS.SESSION_ID, hash);
    } catch (error) {
      console.error('Error setting session ID:', error);
    }
  }

  /**
   * Generates a session hash from the access token
   * @param accessToken - The access token to hash
   * @returns string - The generated session hash
   */
  private generateSessionHash(accessToken: string): string {
    return CryptoJS.SHA256(accessToken)
      .toString(CryptoJS.enc.Base64)
      .toLowerCase()
      .replace(/[+/-]/g, '');
  }

  /**
   * Retrieves the current user ID
   * @returns string | undefined - The user ID if available
   */
  getUID(): string | undefined {
    const profileId = this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID);
    return profileId || undefined;
  }

  /**
   * Updates the session state
   * @param state - The new session state
   */
  updateSessionState(state: Partial<SessionState>): void {
    this.session$.next({
      ...this.session$.value,
      ...state,
    });
  }
}
